import { diffChars } from 'diff';
import { TestGroup } from '../models/test-group.model';
import { BrowserUtils } from './browser-utils';
import { CreateTestGroup } from './live-number-testing.service';
declare const moment: any;

export class CustomUtils {
  static htmlDecode(str: string): string {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = str;
    return textArea.value;
  }

  static text(text: string, max = 40): string {
    if (!text) {
      return text;
    }
    if (text.length > max) {
      return text.substr(0, max - 1) + '...';
    }

    return text;
  }

  static trimModelFields(model): any {
    return model;
  }

  static cloneObject(obj: object): object {
    const newObj = {};
    for (const i in obj) {
      newObj[i] = obj[i];
    }
    return newObj;
  }

  static strToBytes(str: string): number[] {
    const data = [];
    for (let i = 0; i < str.length; i++) {
      data.push(str.charCodeAt(i));
    }
    return data;
  }

  static strToHex(str: string): string[] {
    const data = [];
    for (let i = 0; i < str.length; i++) {
      data.push(str.charCodeAt(i).toString(16));
    }
    return data;
  }

  static camelCaseToDash(myStr: string): string {
    return myStr.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }

  static generateRandomString(length: number): string {
    return Math.random()
      .toString(36)
      .slice(2, length + 2);
  }

  static generateRandomNumber(limit = 10000): number {
    return Math.round(Math.random() * limit);
  }

  static secToHumanTime(sec: number): string {
    const hours = Math.floor(sec / 3600);
    const minutes = Math.floor((sec - hours * 3600) / 60);
    const seconds = sec - hours * 3600 - minutes * 60;
    const parts = [];
    if (hours) {
      parts.push(hours + 'h');
    }
    if (minutes) {
      parts.push(minutes + 'm');
    }
    if (seconds || !parts.length) {
      parts.push(seconds + 's');
    }
    return parts.join(' ');
  }

  static getInitials(fullName): string {
    const allNames = fullName.trim().split(' ');
    const initials = allNames.reduce((acc, curr, index) => {
      if (index === 0 || index === allNames.length - 1) {
        acc = `${acc}${curr.charAt(0).toUpperCase()}`;
      }
      return acc;
    }, '');
    return initials;
  }

  static escapeHTML(html: string): string {
    const replace = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;',
    };
    return html.replace(/[&<>'"]/g, tag => replace[tag] || tag);
  }

  static getMappedTestToTestGroupStructure(test, setting: TestGroup): CreateTestGroup {
    const testGroup: CreateTestGroup = {
      contentTexts: [
        {
          custom: false,
          senderId: test.senderSent,
          text: test.textSent,
          telqIdType: test.telqIdType,
          telqIdLength: test.telqIdLength,
          telqIdCase: test.telqIdCase,
        },
      ],
      destinations: [test.destinationDto],
      sessionsSuppliers: [
        {
          sessionId: test.sessionId,
          supplierId: test.supplierDto.id,
        },
      ],
      userId: null,
      udh: setting.udhDtos,
      tlv: setting.tlvDtos,
      srcNpi: setting.srcNpi,
      srcTon: setting.srcTon,
      protocolId: setting.protocolId,
      encodingId: setting.encodingId,
      ttl: setting.ttl,
      validityPeriod: setting.validityPeriod,
      scheduleDeliveryTime: setting.scheduleDeliveryTime,
      replaceIfPresent: setting.replaceIfPresent,
      testRepeatsPerIteration: setting.testRepeatsPerIteration,
      amountX: setting.amountX || 1,
      commentText: setting.commentText,
    };
    if (setting.flashCallDetection || test.flashCallDetection) {
      testGroup.flashCallDetection = true;
    }
    return testGroup;
  }

  static isTestWithinHours(testCreatedAt: string | Date, maxHours = 24): boolean {
    if (!testCreatedAt) {
      return false;
    }

    const testDate = moment.utc(testCreatedAt);
    const now = moment.utc();

    if (!testDate.isValid()) {
      return false;
    }

    const hoursDiff = now.diff(testDate, 'hours', true);

    return hoursDiff <= maxHours && hoursDiff >= 0;
  }

  static getIconForStatus(statusID: string): string {
    switch (statusID) {
      case 'SCHEDULED':
        return 'far fa-meh text-accent-a2';
      case 'ENROUTE':
        return 'far fa-meh text-accent-a2';
      case 'DELIVERED':
        return 'far fa-smile text-success-s2';
      case 'RECEIVED':
        return 'far fa-smile text-success-s2';
      case 'EXPIRED':
        return 'far fa-frown text-error-r3';
      case 'DELETED':
        return 'far fa-frown text-error-r3';
      case 'UNDELIVERABLE':
        return 'far fa-frown text-error-r3';
      case 'ACCEPTED':
        return 'far fa-meh text-accent-a2';
      case 'UNKNOWN':
        return 'far fa-meh text-accent-a2';
      case 'REJECTED':
        return 'far fa-frown text-error-r3';
      case 'SKIPPED':
        return 'far fa-frown text-error-r3';
      case 'PENDING':
        return 'far fa-meh text-accent-a2';
      case 'NO_DLR_RECEIVED':
        return 'far fa-frown text-accent-a2';
      case 'PARTIALLY_DELIVERED':
        return 'far fa-smile text-accent-a2';
      case 'SUBMIT_SM_FAILED':
        return 'far fa-frown text-error-r3';
      case 'WAIT':
        return 'icon-wait spin';
      case 'SENDER_REPLACED':
        return 'icon-attention text-accent-a2';
      case 'TEXT_REPLACED':
        return 'icon-attention text-accent-a2';
      case 'CALLER_ID_REPLACED':
        return 'icon-attention text-accent-a2';
      case 'SMPP_SESSION_OFFLINE':
        return 'icon-smpp-connection-offline text-error-r3';
      case 'POSITIVE':
        return 'icon-positive text-success-s2';
      case 'NOT_DELIVERED':
        return 'icon-negative text-error-r3';
      case 'TEST_NUMBER_NOT_AVAILABLE':
        return 'icon-test-number-not-available text-accent-a2';
      case 'INTERNAL_ERROR':
        return 'icon-bug-red ms-0_2 fs-4 text-error-r3';
      case 'TEST_NUMBER_OFFLINE':
        return 'icon-negative text-accent-a2';
      case 'NUMBER_OFFLINE':
        return 'icon-negative text-accent-a2';
      case 'NETWORK_OFFLINE':
        return 'icon-network-offline text-error-r3';
      case 'FLASH_CALL_DETECTED':
        return 'icon-flash-call';
      case 'SENT':
        return 'icon-done text-success-s2';
      case 'SENT_ERROR':
        return 'icon-negative text-error-r2';
      case 'UNKNOWN_ERROR':
        return 'icon-enroute-unknown text-accent-a2';
      case 'CANCELED':
        return 'icon-no-dlr-received text-error-r2';
      default:
        return '-';
    }
  }

  static getGuestUrls(): string[] {
    return ['/login', '/reset-password', '/registration', '/email/verify', '/token/recovery'];
  }

  static createId(idType: string, idLength: number): string {
    let possibleValues = '';

    switch (idType) {
      case 'ALPHA':
        possibleValues = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
        break;
      case 'NUMERIC':
        possibleValues = '0123456789';
        break;
      case 'ALPHA_NUMERIC':
        possibleValues = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        break;
      case 'WHATSAPP_CODE':
        return '123-456';
      case 'INSTAGRAM_CODE':
        return '123 456';
    }

    let result = '';
    for (let i = 0; i < idLength; i++) {
      result += possibleValues.charAt(Math.floor(Math.random() * possibleValues.length));
    }
    return result;
  }

  static generateId(telqIdCase: string, telqIdLength: number, telqIdType: string, value: string): string {
    let id = this.createId(telqIdType, telqIdLength);
    switch (telqIdCase) {
      case 'UPPER':
        id = id.toUpperCase();
        break;
      case 'LOWER':
        id = id.toLowerCase();
        break;
    }
    return value.replace('{TEST_ID_TEXT}', id);
  }

  static extractDomain(url: string): string {
    if (url) {
      const domainRegex = /^(?:https?:\/\/)?(?:www\.)?([^\/]+)/;
      const match = url.match(domainRegex);
      return match ? `@${match[1]}` : '';
    }
    return '';
  }

  static formatDate(date: string | Date): string[] {
    return moment(date).format('DD/MM/YY HH:mm:ss').split(' ');
  }

  static formatRelativeDate(date: string | Date): string {
    return moment(date).fromNow();
  }

  static formatDateIntoUTC(date: string): string {
    const utcDate = moment.utc(date).format('DD/MM/YY HH:mm:ss').split(' ');
    return `<div class="two-lines"><span>${utcDate[1]}<br>${utcDate[0]}</span></div>`;
  }

  static sortDays(daysToSort: string[]): string[] {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return daysToSort.sort((a, b) => {
      const dayAIndex = days.findIndex(d => d.toLowerCase() === a.trim().toLowerCase());
      const dayBIndex = days.findIndex(d => d.toLowerCase() === b.trim().toLowerCase());
      return dayAIndex - dayBIndex;
    });
  }

  static getTimezone(): string {
    const offset = moment().utcOffset();
    const sign = offset >= 0 ? '+' : '-';
    const absOffset = Math.abs(offset);
    const hours = Math.floor(absOffset / 60);
    const minutes = absOffset % 60;

    const formattedOffset = `UTC/GMT${sign}${hours}:${minutes < 10 ? '0' : ''}${minutes}`;
    return formattedOffset;
  }

  static decimalToTimeFormat(decimal: number): string {
    const hours = Math.floor(decimal);
    const minutes = Math.round((decimal - hours) * 100);
    const paddedHours = hours.toString().padStart(2, '0');
    const paddedMinutes = minutes.toString().padStart(2, '0');
    return `${paddedHours}:${paddedMinutes}`;
  }

  /**
   * Util to parse hour in format "HH:MM" or number (e.g., 9.3 means 9:30, 9.18 means 9:18) to array [HH, MM]
   */
  static parseHourToHoursMinutes = (hour: string | number): string[] => {
    if (typeof hour === 'string' && hour.includes(':')) {
      // format: "HH:MM"
      return hour.split(':').map(_ => _.padStart(2, '0'));
    } else {
      // format: number (e.g., 9.3 means 9:30, 9.18 means 9:18)
      const hourNum = typeof hour === 'number' ? hour : parseFloat(hour);
      const hours = Math.floor(hourNum);
      const minutes = Math.round((hourNum - hours) * 100);
      return [hours.toString().padStart(2, '0'), minutes.toString().padStart(2, '0')];
    }
  };

  static isOffsetInBrowserTimezone(offsetInSeconds: number): boolean {
    const browserOffsetInSeconds = moment().utcOffset() * 60;
    return browserOffsetInSeconds === offsetInSeconds;
  }

  static getDiff(value1: string, value2: string): string {
    let html = '';
    const classNameDef = {
      added: 'text-error',
      removed: 'text-normal',
      normal: 'text-sucess',
    };
    diffChars(value1, value2).forEach(block => {
      let state = 'normal';
      let isChanged = false;
      if (block.added) {
        state = 'added';
        isChanged = true;
      } else if (block.removed) {
        state = 'removed';
        isChanged = true;
      }
      let className = classNameDef[state];
      if (isChanged && block.value === ' ') {
        className += ' space-highlight';
      }
      const content = BrowserUtils.sanitizeHTML(block.value);
      html += `<span class="${className}">${content === ' ' ? '&nbsp;' : content}</span>`;
    });
    return html;
  }

  static createDiffObject(title1: string, value1: string, title2: string, value2: string, diff = false, telqId: string | null = null) {
    const isReplace = value1 !== value2;

    let textValue1 = value1 === null ? 'NULL' : value1;
    let textValue2 = value2 === null ? 'NULL' : value2;

    textValue1 = textValue1.replaceAll('\r\n', '\\r\\n\r\n').replaceAll('\n', '\\n\n').replaceAll('\r', '\\r\r').replaceAll('\t', '\\t\t');

    textValue2 = textValue2.replaceAll('\r\n', '\\r\\n\r\n').replaceAll('\n', '\\n\n').replaceAll('\r', '\\r\r').replaceAll('\t', '\\r\t');

    const escapedValue1 = BrowserUtils.sanitizeHTML(textValue1);
    const content = BrowserUtils.sanitizeHTML(textValue2);

    let className = '';
    if (value1 !== null && value2 !== null) {
      className = isReplace ? 'text-error' : 'text-sucess';
    }

    let renderedValue1 = escapedValue1;
    let renderedValue2 = `<span class="${className}">${content}</span>`;

    if (telqId) {
      const telqIdContent = `<span title="Test ID Text" class="text-accent">${telqId}</span>`;
      renderedValue1 = renderedValue1.replace(telqId, telqIdContent);
      renderedValue2 = renderedValue2.replace(telqId, telqIdContent);
    }

    if (renderedValue1.startsWith(' ')) {
      renderedValue1 = renderedValue1.replace(' ', `<span title="Space" class="space-highlight">&nbsp;</span>`);
    }

    if (renderedValue1.endsWith(' ')) {
      renderedValue1 = renderedValue1.replace(/.$/, `<span title="Space" class="space-highlight">&nbsp;</span>`);
    }

    return {
      title: `${title1}/${title2}`,
      title1,
      value1: renderedValue1,
      title2,
      value2: renderedValue2,
      result: diff && value1 !== null && value2 !== null ? this.getDiff(textValue1, textValue2) : null,
    };
  }

  static parseNullableNumber(value: string | number | null | undefined): number | null {
    if (value === null || typeof value === 'undefined' || value === '') {
      return null;
    }

    const parsed = typeof value === 'number' ? value : parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  static splitByWhitespace(value: string): string[] {
    return (value || '')
      .split(/\s+/)
      .map(_ => _.trim())
      .filter(_ => _);
  }
}
